/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { getCachedUserToken } from '../../../../utilities/caching';
import { gotoUrlEnvironment } from '../../../../utilities/helpers';
import { ACCOUNT_REGIONS, loyaltyScreens } from '../../../../utilities/constants';
import {
  SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  USER_REGION_SETTINGS_QUERY_KEY,
} from '../../../../utilities/sharedQueries';
import { useSetting } from '../../../../utilities/sharedHooks/settings';
import { useLedgerFilteredAllowedCreationCurrencies } from '../../../../utilities/sharedHooks/ledgerWallets';
import { CARD_LOAD_CASHBACK } from '../../queries';
import { CASHBACK_DEEPLINK_QUERY_PARAMS, CASHBACK_DEEPLINK_SESSION_KEYS } from './constants';

const CashbackDeeplink = () => {
  const location = useLocation();
  const history = useHistory();

  const { data: userRegionSettings, isLoading: isLoadingRegionSettings } = useQuery(
    USER_REGION_SETTINGS_QUERY_KEY
  );
  const [allowLoyaltyProgramToggle, isLoadingLoyaltyToggle] = useSetting(
    'app_features',
    'feature_use_loyalty_program'
  );
  const [allowCashbackPayoutOnDemand, isLoadingPayoutOnDemand] = useSetting(
    'app_features',
    'allow_cashback_payout_on_demand'
  );
  const { data: cashbackData, isLoading: isLoadingCashbackData } = useQuery(CARD_LOAD_CASHBACK);
  const { data: allowedCurrencies, isLoading: isLoadingAllowedCurrencies } =
    useLedgerFilteredAllowedCreationCurrencies([
      SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
      {
        allow_as_cashback_reward: true,
      },
    ]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const appId = searchParams.get(CASHBACK_DEEPLINK_QUERY_PARAMS.APP_ID);
    const pathname = location.pathname.replace(/\/+$/, '');

    const isRowApp = appId === process.env.REACT_APP_ROW_APP_NAME;
    const isUsApp = appId === process.env.REACT_APP_US_APP_NAME;
    const isValidAppId = isRowApp || isUsApp;
    const isOnRowHost = window.location.hostname === process.env.REACT_APP_FE_BASE_HOSTNAME;
    const isOnUsHost = window.location.hostname === process.env.REACT_APP_US_FE_BASE_HOSTNAME;

    if (isValidAppId && !(isRowApp && isOnRowHost) && !isOnUsHost) {
      gotoUrlEnvironment(ACCOUNT_REGIONS.US, `${pathname.slice(1)}${location.search}`);
      return;
    }

    sessionStorage.setItem(
      CASHBACK_DEEPLINK_SESSION_KEYS.RETURN_URL,
      `${pathname}${location.search}`
    );

    const token = getCachedUserToken();
    if (!token) {
      history.push('/onboarding/login');
      return;
    }

    if (
      isLoadingRegionSettings ||
      isLoadingLoyaltyToggle ||
      isLoadingPayoutOnDemand ||
      allowLoyaltyProgramToggle === null ||
      allowCashbackPayoutOnDemand === null ||
      isLoadingCashbackData ||
      isLoadingAllowedCurrencies
    ) {
      return;
    }

    sessionStorage.removeItem(CASHBACK_DEEPLINK_SESSION_KEYS.RETURN_URL);

    const allowLoyaltyProgram = !!(
      userRegionSettings?.allow_loyalty_program && allowLoyaltyProgramToggle
    );
    if (!allowLoyaltyProgram || !allowCashbackPayoutOnDemand) {
      history.push('/dashboard/card/details');
      return;
    }

    history.push('/dashboard/card/loyalty', {
      initScreen: cashbackData?.cashback_currency
        ? loyaltyScreens.CLAIM_REWARD
        : loyaltyScreens.SELECT_CASHBACK,
      allowedCurrencies,
      cashbackData,
    });
  }, [
    userRegionSettings,
    allowLoyaltyProgramToggle,
    allowCashbackPayoutOnDemand,
    cashbackData,
    allowedCurrencies,
    isLoadingRegionSettings,
    isLoadingLoyaltyToggle,
    isLoadingPayoutOnDemand,
    isLoadingCashbackData,
    isLoadingAllowedCurrencies,
  ]);

  return null;
};

export default CashbackDeeplink;
